import { useEffect, useMemo, useRef, useState } from 'react';
import { useEndpointStatus } from '@/hooks/use-endpoint-status';
import { isEmbedded, postToHost, subscribeToHost } from '@/lib/embed-bridge';
import { useEmbedContext } from '@/lib/embed-context';
import type { EmbedConfig, EmbedMode, HostToIframe } from '@/lib/embed-protocol';

/**
 * React hook exposing the embed-side postMessage bridge.
 *
 * Usage:
 *   const { embedded, mode, onHostMessage, sendReady, sendResize, ... } = useEmbedBridge()
 *
 * The hook:
 *   1. Detects whether we're inside an iframe
 *   2. Reads the embed mode from the `embed=floating|inline` URL param
 *   3. Wires listeners for host→iframe messages via `onHostMessage`
 *   4. Exposes helpers for iframe→host notifications
 */

type HostHandler = (msg: HostToIframe) => void;

export function useEmbedBridge() {
	// The district and placement used to be read straight off window.location.
	// In an in-page mount that is the school's URL — a school page that happens
	// to carry its own `?d=` would retarget the chat at another district — so
	// both now come from the embed context, which reads the URL only for the SPA
	// and the iframe.
	const ctx = useEmbedContext();
	// Keyed on the context rather than on window.top: an element inside a school
	// page that is itself framed (an LMS, a CMS preview) is not "embedded" in the
	// loader's sense, and must neither announce itself to that frame nor accept
	// `reachout:navigate` from it. postToHost/subscribeToHost guard the same way.
	const embedded = ctx.source === 'iframe' && isEmbedded();
	const [gaggleToken, setGaggleToken] = useState<string | null>(null);
	const [theme, setTheme] = useState<'light' | 'dark' | null>(null);
	const [opened, setOpened] = useState(false);

	// Host-message subscribers registered by feature code (e.g. chat page).
	const extraHandlersRef = useRef<Set<HostHandler>>(new Set());

	const mode = useMemo<EmbedMode | null>(() => {
		if (!embedded) return null;
		return ctx.embedMode;
	}, [embedded, ctx.embedMode]);

	const districtUuid = ctx.district;

	useEffect(() => {
		if (!embedded) return;
		const unsub = subscribeToHost((msg) => {
			switch (msg.type) {
				case 'reachout:set-user':
					setGaggleToken(msg.gaggleToken);
					break;
				case 'reachout:set-theme':
					setTheme(msg.theme);
					break;
				case 'reachout:open':
					setOpened(true);
					break;
				case 'reachout:close':
					setOpened(false);
					break;
				case 'reachout:navigate':
					// Consumers can hook via extraHandlersRef
					break;
			}
			for (const h of extraHandlersRef.current) h(msg);
		});
		return unsub;
	}, [embedded]);

	// Auto-emit `ready` once on mount when embedded.
	useEffect(() => {
		if (!embedded) return;
		postToHost({
			type: 'reachout:ready',
			chatOrigin: window.location.origin,
		});
	}, [embedded]);

	// Report the district's presentation config back to the loader.
	//
	// The loader can't fetch this itself — `endpoint-status` isn't CORS-open to
	// arbitrary school origins, and its URL lives in a global the loader can't
	// read cross-origin. We already probe it here on the chat origin, so the
	// result travels back over the bridge instead. Until it lands the loader
	// paints from its localStorage cache, so this is a re-theme rather than a
	// blocking dependency.
	const endpointStatus = useEndpointStatus(embedded ? districtUuid : null);
	useEffect(() => {
		if (!embedded) return;
		if (endpointStatus.state === 'loading') return;

		const config: EmbedConfig =
			endpointStatus.state === 'enabled'
				? {
						enabled: true,
						features: endpointStatus.features,
						chatMode: endpointStatus.chatMode,
						accent: endpointStatus.widgetColor,
						label: endpointStatus.launcherLabel,
						launcherStyle: endpointStatus.launcherStyle,
						icon: endpointStatus.launcherIcon,
						height: endpointStatus.launcherHeight,
						position: endpointStatus.launcherPosition,
					}
				: // Disabled or unreachable: report enabled:false so a host page can
					// react, but send no presentation — a kill-switched district
					// shouldn't have its branding cached on the school's origin.
					{ enabled: endpointStatus.state !== 'disabled', features: [] };

		postToHost({ type: 'reachout:config', config });
	}, [embedded, endpointStatus]);

	// Escape inside a floating panel asks the host to close it — the host's own
	// Escape handler cannot see keystrokes in this frame. A dialog of ours (End
	// chat, a fullscreen card) gets the key first: Radix closes it, and only a
	// second Escape leaves the chat.
	useEffect(() => {
		if (!embedded || mode !== 'floating') return;
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key !== 'Escape' || event.defaultPrevented) return;
			if (
				document.querySelector(
					'[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]',
				)
			)
				return;
			postToHost({ type: 'reachout:close-request' });
		};
		document.addEventListener('keydown', onKeyDown);
		return () => document.removeEventListener('keydown', onKeyDown);
	}, [embedded, mode]);

	// Auto-emit `resize` on document body size changes (inline + ambient modes
	// size the iframe to fit the host page's layout).
	useEffect(() => {
		if (!embedded || (mode !== 'inline' && mode !== 'ambient')) return;
		const emit = () => {
			postToHost({
				type: 'reachout:resize',
				height: document.documentElement.scrollHeight,
			});
		};
		emit();
		const ro = new ResizeObserver(emit);
		ro.observe(document.documentElement);
		return () => ro.disconnect();
	}, [embedded, mode]);

	return {
		embedded,
		mode,
		districtUuid,
		gaggleToken,
		theme,
		opened,
		sendMessageSent: () =>
			postToHost({
				type: 'reachout:message-sent',
				timestamp: new Date().toISOString(),
			}),
		sendSessionEnded: (reason: 'user-closed' | 'agent-ended' | 'timeout') =>
			postToHost({ type: 'reachout:session-ended', reason }),
		onHostMessage: (handler: HostHandler): (() => void) => {
			extraHandlersRef.current.add(handler);
			return () => {
				extraHandlersRef.current.delete(handler);
			};
		},
	};
}
